import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import HexagonOutlinedIcon from '@mui/icons-material/HexagonOutlined';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import React from 'react';
import { useHistory } from 'react-router-dom';

interface Props {
  open: boolean;
  onClose: () => void;
  baseUrl: string;
  onNavigate?: () => void;
  companyId?: number;
  returnTo?: string;
}

export const VirtualFenceCreateChooser = ({ open, onClose, baseUrl, onNavigate, companyId, returnTo }: Props) => {
  const history = useHistory();
  const theme = useTheme();

  const navigate = (path: string) => {
    onNavigate?.();
    onClose();
    history.push(companyId != null ? `${path}?companyId=${companyId}` : path, returnTo ? { returnTo } : undefined);
  };

  const cardSx = {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 1.5,
    p: 3,
    border: '2px solid',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'border-color 0.15s, background-color 0.15s',
    '&:hover': {
      borderColor: theme.palette.secondary.main,
      backgroundColor:
        theme.palette.mode === 'dark'
          ? 'rgba(255,255,255,0.04)'
          : 'rgba(0,0,0,0.02)',
    },
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Typography variant="h6" fontWeight={600}>
          Criar nova cerca virtual
        </Typography>
        <IconButton onClick={onClose} size="small" aria-label="fechar">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pb: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Escolha o tipo de cerca que deseja criar:
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2,
          }}
        >
          {/* ── Circular ──────────────────────────────────────────────── */}
          <Box
            sx={{ ...cardSx, borderColor: theme.palette.divider }}
            onClick={() => navigate(`${baseUrl}/new`)}
            role="button"
            tabIndex={0}
            onKeyDown={e => e.key === 'Enter' && navigate(`${baseUrl}/new`)}
          >
            <CircleOutlinedIcon sx={{ fontSize: 48, color: theme.palette.info.main }} />
            <Typography variant="subtitle1" fontWeight={600}>
              Circular
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ fontSize: '0.8rem' }}>
              Definida por centro e raio. Ideal para áreas simples.
            </Typography>
          </Box>

          {/* ── Poligonal ─────────────────────────────────────────────── */}
          <Box
            sx={{ ...cardSx, borderColor: theme.palette.divider }}
            onClick={() => navigate(`${baseUrl}/polygon/new`)}
            role="button"
            tabIndex={0}
            onKeyDown={e => e.key === 'Enter' && navigate(`${baseUrl}/polygon/new`)}
          >
            <HexagonOutlinedIcon sx={{ fontSize: 48, color: theme.palette.success.main }} />
            <Typography variant="subtitle1" fontWeight={600}>
              Poligonal
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ fontSize: '0.8rem' }}>
              Desenhada por pontos no mapa. Ideal para áreas irregulares.
            </Typography>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default VirtualFenceCreateChooser;
